import * as zod from 'zod';

import { InputSafeguardRule, OutputSafeguardRule } from '@sb/routine-runner';

export const SafetySettingsPreset = zod.enum([
  'none',
  'safest',
  'safer',
  'default',
  'faster',
  'fastest',
]);

export type SafetySettingsPreset = zod.infer<typeof SafetySettingsPreset>;

// Mirrors C++ DynamicCollisionDetector::Config. Keep names + units in sync.
//   Per-joint (shown in UI):  headroomNm, maxThresholdNm, cmdStepHeadroomNm
//   Arm-wide (UI sets one value, written to every joint): lagMs, windowMs
export const DynamicCollisionDetectorConfig = zod.object({
  headroomNm: zod.number().nonnegative().nullish(),
  maxThresholdNm: zod.number().positive().nullish(),
  cmdStepHeadroomNm: zod.number().nonnegative().nullish(),
  lagMs: zod.number().int().positive().nullish(),
  windowMs: zod.number().int().positive().nullish(),
});

export type DynamicCollisionDetectorConfig = zod.infer<
  typeof DynamicCollisionDetectorConfig
>;

export const SafetySettingsData = zod.object({
  id: zod.string(),
  robotID: zod.string(),
  factoryPreset: SafetySettingsPreset,
  maxJointSpeeds: zod.number().nullable().array(),
  maxTooltipSpeed: zod.number().nullable(),
  maxTooltipAcceleration: zod.number().nullable(),
  maxJointAccelerations: zod.number().nullable().array(),
  maxJointTorques: zod.number().nullable().array(),
  slowJointSpeeds: zod.number().nullable().array(),
  slowTooltipSpeed: zod.number().nullable(),
  slowJointAccelerations: zod.number().nullable().array(),
  slowJointTorques: zod.number().nullable().array(),
  jointStoppingAccelerations: zod.number().nullable().array(),
  inputSafeguardRules: InputSafeguardRule.array(),
  outputSafeguardRules: OutputSafeguardRule.array(),
  collisionThresholds: zod.number().nullable().array(),
  dynamicCollisionDetectorConfig: DynamicCollisionDetectorConfig.nullable()
    .array()
    .nullable()
    .optional(),
  dynamicCollisionDetectorUseDefaults: zod.boolean().nullable().optional(),
  accelerationThreshold: zod.number().nullable().array(),
  jerkLimit: zod.number().nullable(),
  updatedAt: zod.string(),
  baseAccelerationScaling: zod.number().nullable(),
  baseVelocityScaling: zod.number().nullable(),
});

export type SafetySettingsData = zod.infer<typeof SafetySettingsData>;
